// Light-DOM custom element that powers the partner matrix: clicking a logo activates it (showing the
// matching description on the left), and the active partner rotates automatically every ~20s. Mirrors the
// <vp-path> approach (no shadow DOM, so the editor can still address every inner node by class) and goes
// "static" inside the editor preview frame or for reduced-motion users — there it reveals all
// descriptions so each one stays selectable/editable and nothing moves on its own.

const ROTATE_INTERVAL_MS = 20000;

class VpPartnerMatrixElement extends HTMLElement {
  private logos: HTMLElement[] = [];
  private descriptions: HTMLElement[] = [];
  private progressSegments: HTMLElement[] = [];
  private progressFills: HTMLElement[] = [];
  private activeIndex = 0;
  private timer = 0;
  private fillAnimation?: Animation;
  private isStatic = false;

  connectedCallback() {
    this.isStatic =
      !!this.closest('.vp-editor-frame') ||
      (typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

    this.logos = Array.from(this.querySelectorAll<HTMLElement>('.vp-partner-logo'));
    this.descriptions = Array.from(this.querySelectorAll<HTMLElement>('.vp-partner-desc'));
    this.progressSegments = Array.from(this.querySelectorAll<HTMLElement>('.vp-partner-progress-seg'));
    this.progressFills = Array.from(this.querySelectorAll<HTMLElement>('.vp-partner-progress-fill'));
    if (!this.logos.length) return;

    if (this.isStatic) {
      // Editor / reduced motion: keep every description visible & editable, no auto-rotation.
      this.classList.add('is-static');
      return;
    }

    const initial = this.logos.findIndex(logo => logo.classList.contains('is-active'));
    this.setActive(initial >= 0 ? initial : 0);

    this.logos.forEach((logo, index) => {
      logo.addEventListener('click', this.onPickClick);
      logo.dataset.index = String(index);
    });
    this.progressSegments.forEach((seg, index) => {
      seg.addEventListener('click', this.onPickClick);
      seg.dataset.index = String(index);
    });

    this.startRotation();
  }

  disconnectedCallback() {
    this.stopRotation();
    this.fillAnimation?.cancel();
    this.logos.forEach(logo => logo.removeEventListener('click', this.onPickClick));
    this.progressSegments.forEach(seg => seg.removeEventListener('click', this.onPickClick));
  }

  private onPickClick = (event: Event) => {
    // Let the editor handle selection clicks; only drive the matrix on the live page.
    if (this.isStatic) return;
    const target = (event.currentTarget as HTMLElement) ?? null;
    const index = target ? Number(target.dataset.index) : NaN;
    if (Number.isNaN(index)) return;
    this.setActive(index);
    this.startRotation(); // reset the timer so a manual pick stays put for a full interval
  };

  private setActive(index: number) {
    this.activeIndex = index;
    this.logos.forEach((logo, i) => {
      const active = i === index;
      logo.classList.toggle('is-active', active);
      logo.setAttribute('aria-pressed', String(active));
    });
    this.descriptions.forEach((desc, i) => desc.classList.toggle('is-active', i === index));
    this.progressSegments.forEach((seg, i) => seg.classList.toggle('is-active', i === index));
    this.runProgress(index);
  }

  // Sweeps the active dot's fill from 0 → 100% across the rotation interval, so the indicator visibly
  // "counts down" to the next partner. Restarted on every activation (auto or manual click).
  private runProgress(index: number) {
    this.fillAnimation?.cancel();
    this.fillAnimation = undefined;
    if (this.isStatic) return;
    const fill = this.progressFills[index];
    if (!fill || typeof fill.animate !== 'function') return;
    this.fillAnimation = fill.animate(
      [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
      { duration: ROTATE_INTERVAL_MS, easing: 'linear', fill: 'forwards' },
    );
  }

  private startRotation() {
    this.stopRotation();
    if (this.isStatic || this.logos.length < 2) return;
    this.timer = window.setInterval(() => {
      this.setActive((this.activeIndex + 1) % this.logos.length);
    }, ROTATE_INTERVAL_MS);
  }

  private stopRotation() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = 0;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('vp-partner-matrix')) {
  customElements.define('vp-partner-matrix', VpPartnerMatrixElement);
}
